import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import type { SelfExamMarksResponse } from '@bbl/validation';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { Skeleton } from '../components/ui/Skeleton';
import { SelfMarksList } from '../components/exams';
import { GatedSection } from '../components/shared/GatedSection';
import type { ChildSummary } from '../components/me/types';

export const Route = createFileRoute('/_authed/me/children/$studentId/marks')({
  component: ChildMarksPage,
});

interface ChildrenResponse {
  items: ChildSummary[];
}

function ChildMarksPage() {
  const { studentId } = Route.useParams();
  const role = useAuthStore((s) => s.user?.primaryRole);
  const isParent = role === 'parent';

  const childrenQuery = useQuery({
    queryKey: ['me', 'children'] as const,
    queryFn: ({ signal }) =>
      apiRequest<ChildrenResponse>('/api/me/children', { signal }),
    enabled: isParent,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  const marksQuery = useQuery({
    queryKey: ['me', 'child-marks', studentId] as const,
    queryFn: ({ signal }) =>
      apiRequest<SelfExamMarksResponse>(
        `/api/me/children/${encodeURIComponent(studentId)}/exam-marks`,
        { signal },
      ),
    enabled: isParent,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  if (role && !isParent) {
    return (
      <ListShell title="Child marks" description="Parent self-service.">
        <EmptyState
          title="This page is for parents"
          description="Students can view their own marks from the My marks page."
          action={
            <Link to="/dashboard">
              <Button variant="secondary">Back to dashboard</Button>
            </Link>
          }
        />
      </ListShell>
    );
  }

  const child = childrenQuery.data?.items.find((c) => c.id === studentId);

  return (
    <ListShell
      title={child ? `${child.name} — marks` : 'Marks'}
      description={
        child
          ? `Published exam results for ${child.name}.`
          : "Published exam results for your child."
      }
      primaryAction={
        <Link
          to="/me/children"
          className="inline-flex items-center gap-1 text-sm text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
        >
          <ArrowLeft size={14} aria-hidden="true" />
          Back to children
        </Link>
      }
    >
      <GatedSection
        error={marksQuery.error}
        title="Child marks"
        description="Marks will appear here once exam cycles are enabled and your child's school publishes them."
        onRetry={() => marksQuery.refetch()}
      >
        {marksQuery.isLoading || !marksQuery.data ? (
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : (
          <SelfMarksList items={marksQuery.data.items} />
        )}
      </GatedSection>
    </ListShell>
  );
}
